import { Component, type ErrorInfo, type FormEvent, type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/functions/_generated/api';
import type { Id } from '../../../convex/functions/_generated/dataModel';
import { adminSession } from '@/lib/api';
import { friendlyError } from '@/lib/errors';
import { activeConfig } from '@/lib/environments';
import { InfoModal } from '@/components/Modals';

/**
 * /dashboard/creators — paid creator campaign tracker.
 *
 * One reactive query (api.admin.creators.list) returns every campaign
 * with its funnel counts; Convex pushes updates as events land, so
 * this page doubles as the live "is the campaign working?" board:
 *
 *   visits → ratings → signups, CPI = fee ÷ signups
 *
 * The add form keys on the creator's card SLUG (pasted from their
 * published card URL) — the backend resolves it to their account.
 * Status transitions (booked → active → done/cancelled) are inline
 * selects; everything else is edited by re-adding (slug is immutable
 * server-side to protect historical attribution).
 */

type Status = 'booked' | 'active' | 'done' | 'cancelled';

const STATUS_TONE: Record<Status, string> = {
  booked: '#a86b00',
  active: '#2e7d32',
  done: 'var(--ink-3)',
  cancelled: '#a8331f',
};

export function CreatorsPage() {
  return (
    <CreatorsErrorBoundary>
      <CreatorsInner />
    </CreatorsErrorBoundary>
  );
}

function CreatorsInner() {
  const token = adminSession.getToken() ?? '';
  const data = useQuery(api.admin.creators.list, { token });
  const updateMut = useMutation(api.admin.creators.update);
  const [helpOpen, setHelpOpen] = useState(false);

  const setStatus = (campaignId: Id<'creatorCampaigns'>, status: Status) => {
    updateMut({ token, campaignId, status }).catch(() => {});
  };

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <h2 className="serif" style={{ fontSize: 30, fontWeight: 500, fontStyle: 'italic', margin: 0 }}>
            Creator campaigns
          </h2>
          <div style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 4 }}>
            Live funnel per creator — visits → ratings → signups, CPI = fee ÷ signups.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          style={helpBtn}
          aria-label="How creator campaigns work"
        >
          <span aria-hidden style={helpGlyph}>?</span> How this works
        </button>
      </header>

      <CreatorsHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />

      <AddCreatorForm />

      {!data ? (
        <div style={{ color: 'var(--ink-3)', padding: 32, textAlign: 'center' }}>Loading…</div>
      ) : data.campaigns.length === 0 ? (
        <Hint>No creators booked yet. Add the first one above once their card is published.</Hint>
      ) : (
        <Section title={`Campaigns (${data.campaigns.length})`}>
          <div style={{ overflowX: 'auto' }}>
            <table style={tbl}>
              <thead>
                <tr>
                  <th style={th}>Creator</th>
                  <th style={th}>Platform</th>
                  <th style={th}>Metro</th>
                  <th style={th}>Card</th>
                  <th style={{ ...th, textAlign: 'right' }}>Fee</th>
                  <th style={{ ...th, textAlign: 'right' }}>Visits</th>
                  <th style={{ ...th, textAlign: 'right' }}>Ratings</th>
                  <th style={{ ...th, textAlign: 'right' }}>Signups</th>
                  <th style={{ ...th, textAlign: 'right' }}>CPI</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.campaigns.map(({ campaign, visits, ratings, signups, cpiUsd }) => (
                  <tr key={campaign._id}>
                    <td style={td}>
                      <Link to={`/dashboard/creators/${campaign._id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <div style={{ fontWeight: 600, color: 'var(--coral, #e86a4c)' }}>{campaign.name}</div>
                        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{campaign.handle}</div>
                      </Link>
                    </td>
                    <td style={td}><span className="mono" style={{ fontSize: 11 }}>{campaign.platform}</span></td>
                    <td style={td}><span className="mono" style={{ fontSize: 11, textTransform: 'uppercase' }}>{campaign.metro}</span></td>
                    <td style={td}>
                      <a
                        href={`${activeConfig().webOrigin}/p/${campaign.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mono"
                        style={{ fontSize: 11, color: 'var(--coral, #e86a4c)' }}
                      >
                        /p/{campaign.slug}
                      </a>
                    </td>
                    <td style={{ ...td, textAlign: 'right' }} className="mono">${campaign.feeUsd.toLocaleString()}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{visits.toLocaleString()}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{ratings.toLocaleString()}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{signups.toLocaleString()}</td>
                    <td style={{ ...td, textAlign: 'right' }} className="mono">
                      {cpiUsd != null ? `$${cpiUsd.toFixed(2)}` : '—'}
                    </td>
                    <td style={td}>
                      <select
                        value={campaign.status}
                        onChange={(e) => setStatus(campaign._id, e.target.value as Status)}
                        style={{ ...selectStyle, color: STATUS_TONE[campaign.status as Status] ?? 'var(--ink)' }}
                        aria-label={`Status for ${campaign.name}`}
                      >
                        {(['booked', 'active', 'done', 'cancelled'] as const).map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}

function AddCreatorForm() {
  const token = adminSession.getToken() ?? '';
  const createMut = useMutation(api.admin.creators.create);
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [platform, setPlatform] = useState('tiktok');
  const [metro, setMetro] = useState('nyc');
  const [fee, setFee] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setOkMsg(null);
    setBusy(true);
    try {
      // Accept a pasted URL ("https://wydm.app/p/maya?utm=…") as
      // readily as a bare slug — operators copy from the browser bar.
      const bareSlug = slug.trim().replace(/^https?:\/\/[^/]+\/p\//i, '').split(/[?#/]/)[0];
      await createMut({
        token,
        slug: bareSlug,
        name,
        handle,
        platform,
        metro,
        feeUsd: Number(fee),
        notes: notes.trim() || undefined,
      });
      setOkMsg(`Campaign created for /p/${bareSlug}.`);
      setSlug(''); setName(''); setHandle(''); setFee(''); setNotes('');
    } catch (err) {
      setError(friendlyError(err, 'createCreator'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div style={{ marginBottom: 20 }}>
        <button type="button" onClick={() => setOpen(true)} style={primaryBtn}>
          + Add creator
        </button>
      </div>
    );
  }

  return (
    <Section title="Add creator">
      <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <Field label="Card slug or URL">
          <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="maya or https://wydm.app/p/maya" style={input} required />
        </Field>
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Maya R." style={input} required />
        </Field>
        <Field label="Handle">
          <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@mayadates" style={input} required />
        </Field>
        <Field label="Platform">
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} style={input}>
            <option value="tiktok">tiktok</option>
            <option value="instagram">instagram</option>
            <option value="youtube">youtube</option>
            <option value="other">other</option>
          </select>
        </Field>
        <Field label="Metro">
          <select value={metro} onChange={(e) => setMetro(e.target.value)} style={input}>
            <option value="nyc">nyc</option>
            <option value="london">london</option>
            <option value="austin">austin</option>
            <option value="other">other</option>
          </select>
        </Field>
        <Field label="Fee (USD)">
          <input value={fee} onChange={(e) => setFee(e.target.value)} placeholder="150" inputMode="decimal" style={input} required />
        </Field>
        <Field label="Notes (optional)">
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="2 videos + story, posts w/c 24th" style={input} />
        </Field>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <button type="submit" disabled={busy} style={primaryBtn}>
            {busy ? 'Adding…' : 'Add campaign'}
          </button>
          <button type="button" onClick={() => setOpen(false)} style={ghostBtn}>
            Cancel
          </button>
        </div>
      </form>
      {error && <div style={{ marginTop: 10, color: '#a8331f', fontSize: 13 }}>{error}</div>}
      {okMsg && <div style={{ marginTop: 10, color: '#2e7d32', fontSize: 13 }}>{okMsg}</div>}
    </Section>
  );
}

function CreatorsHelpModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <InfoModal open={open} onClose={onClose} title="How creator campaigns work">
      <HelpSection title="What this page is">
        Your per-creator marketing funnel. Every paid creator gets a row;
        the app automatically tracks how their audience moves from
        viewing their card → rating it → signing up, and computes their
        cost per install (CPI = fee ÷ signups). <strong>Click a creator's
        name</strong> to open their detail page: shareable links, posts,
        agreements, and bonuses.
      </HelpSection>

      <HelpSection title="Booking a creator">
        <ol style={helpList}>
          <li>The creator downloads the app and publishes their own card (that card <em>is</em> the campaign content — their audience will rate it).</li>
          <li>Click <strong>+ Add creator</strong> and paste their card URL (or bare slug), name, handle, platform, metro, and agreed fee.</li>
          <li>Open their detail page and send them their links: the <strong>card link</strong> (<span className="mono">wydm.app/p/&lt;slug&gt;</span>) for videos, and the <strong>affiliate link</strong> (<span className="mono">/join?ref=CODE</span>) for link-in-bio. Both attribute signups to them.</li>
        </ol>
        Register the campaign <strong>before</strong> their post goes live — traffic to an unregistered slug is not tracked retroactively.
      </HelpSection>

      <HelpSection title="Posts">
        On the detail page, register each social post the creator makes
        (TikTok, Instagram, Snapchat, Facebook, YouTube) with its URL and
        an optional note. This is a link registry for your records — no
        view/like analytics are fetched (platform APIs all require the
        creator's own login).
      </HelpSection>

      <HelpSection title="Agreements &amp; bonuses">
        Each creator has a <strong>current agreement</strong> recording the
        commercial structure — <em>fixed monthly</em>, <em>per post</em>, or{' '}
        <em>hybrid</em> — plus any agreed bonuses. When you renegotiate,
        click <strong>New version (supersede)</strong>: the old agreement is
        frozen in history, never edited, and already-hit bonuses stay
        attached to the version that promised them. The history section
        is your audit trail for payout disputes.
        <ul style={{ ...helpList, marginTop: 6 }}>
          <li><strong>Signup bonuses auto-detect</strong> — the moment attributed signups cross a threshold, the bonus flips to <em>hit</em> with a timestamp. Watch the progress bar until then.</li>
          <li><strong>Views bonuses</strong> are tracked externally (we don't fetch view counts): click <strong>Mark hit</strong> when the creator reports the numbers.</li>
          <li>After paying out, click <strong>Mark paid</strong> — hit and paid dates are both preserved.</li>
        </ul>
      </HelpSection>

      <HelpSection title="How attribution works">
        When someone lands on a creator's card, the web app stashes the
        slug (and any <span className="mono">utm_source</span>/<span className="mono">utm_campaign</span> from boosted posts) in the browser. If they later
        sign up — via password, Apple, or Google — the signup is stamped
        to that creator. A referral code beats a slug when both exist;
        attribution is single-shot per user. No pixels, no cookies, no
        PII on events.
      </HelpSection>

      <HelpSection title="Reading the numbers">
        <ul style={helpList}>
          <li><strong>Visits</strong> — card page loads. Directional only: bots can inflate it. Never bill on this.</li>
          <li><strong>Ratings</strong> — verdicts submitted on the creator's card. Shows the audience is actually engaging.</li>
          <li><strong>Signups</strong> — new accounts attributed to the creator. The billing-grade metric.</li>
          <li><strong>CPI</strong> — fee ÷ signups. Shows &ldquo;—&rdquo; until the first signup.</li>
        </ul>
        Status (<em>booked → active → done / cancelled</em>) is bookkeeping only —
        events are recorded regardless, so an evergreen video keeps
        reporting after the campaign ends.
      </HelpSection>

      <HelpSection title="Gotchas">
        <ul style={helpList}>
          <li>A campaign's slug is immutable — to point at a different card, add a new campaign (protects historical attribution).</li>
          <li>Campaign <strong>feeUsd</strong> is the CPI denominator. If a renegotiated retainer should drive CPI instead, update it on the campaign (the agreement records it contractually either way).</li>
          <li>App Store / Play installs don't attribute in-app. Create per-creator campaign links in App Store Connect and UTM links for Play to count those installs.</li>
          <li>Referral rewards (give-a-week) are unaffected: a creator's code can earn them referral weeks <em>and</em> count toward this dashboard.</li>
        </ul>
      </HelpSection>
    </InfoModal>
  );
}

function HelpSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: 16 }}>
        {children}
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>{children}</div>;
}

const tbl: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 10px',
  borderBottom: '1px solid var(--line-strong)',
  fontWeight: 600,
  color: 'var(--ink-3)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 1,
};
const td: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--line)',
  color: 'var(--ink)',
};
const input: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 10,
  border: '1px solid var(--line)',
  background: 'var(--bg)',
  color: 'var(--ink)',
  fontSize: 13,
  fontFamily: 'inherit',
};
const selectStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderRadius: 8,
  border: '1px solid var(--line)',
  background: 'var(--bg)',
  fontSize: 12,
  fontFamily: 'inherit',
  textTransform: 'uppercase',
  letterSpacing: 1,
};
const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 10,
  border: 'none',
  background: 'var(--coral, #e86a4c)',
  color: '#fff',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const ghostBtn: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 10,
  border: '1px solid var(--line)',
  background: 'transparent',
  color: 'var(--ink-3)',
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const helpBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '7px 14px',
  borderRadius: 100,
  border: '1px solid var(--line)',
  background: 'var(--surface)',
  color: 'var(--ink-2)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  flexShrink: 0,
};
const helpGlyph: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  borderRadius: 100,
  background: 'var(--coral, #e86a4c)',
  color: '#fff',
  fontSize: 11,
  fontWeight: 700,
};
const helpList: React.CSSProperties = {
  margin: 0,
  paddingLeft: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

// Same rationale as Health.tsx: useQuery re-throws on render, so the
// only safe inline-error path is a class ErrorBoundary.
interface CreatorsErrorState {
  err: unknown | null;
}

class CreatorsErrorBoundary extends Component<{ children: ReactNode }, CreatorsErrorState> {
  state: CreatorsErrorState = { err: null };
  static getDerivedStateFromError(err: unknown): CreatorsErrorState {
    return { err };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[CreatorsPage]', error, info);
  }
  render() {
    if (this.state.err !== null) {
      return (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '14px 16px', borderRadius: 12, fontSize: 13 }}>
          {friendlyError(this.state.err, 'listCreators')}
        </div>
      );
    }
    return this.props.children;
  }
}
